import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { type FC, memo, useLayoutEffect, useMemo, useRef } from 'react';

import { useDugoutContractInsuranceEntities } from '@/hooks/useDugoutContracts';
import type { ContractedInsuranceEntity, ContractProgramFamilyOption } from '@/types/contract';
import { Spinner, Typography } from '@/ui';
import {
  familyColumnLabel,
  familyTagLabel,
  groupFamiliesByCategory,
  PHASE_TYPE_LEGEND,
  phaseTypeAbbreviation
} from '@/utils/contractLabels';

import './styles.css';
import type { Props } from './types';

const familyCoverage = (entity: ContractedInsuranceEntity, family: ContractProgramFamilyOption) =>
  entity.program_families.find((entityFamily) => entityFamily.program_family_id === family.id)
    ?.coverage ?? 'none';

const AllCovered = () => (
  <span className="font-medium text-uniform-green-700 dark:text-uniform-green-400">
    <FontAwesomeIcon icon={faCheck} className="mr-1" />
    All
  </span>
);

// The contracted phase types of a partly covered family, e.g. "OP · IOP + Dual".
const contractedLevels = (
  entity: ContractedInsuranceEntity,
  family: ContractProgramFamilyOption
) => {
  const programs = entity.contracted_programs.filter(
    (program) => program.program_family_id === family.id && program.is_active
  );
  const levels = [
    ...new Set(programs.map((program) => phaseTypeAbbreviation(program.phase_type)))
  ].join(' · ');
  return programs.some((program) => program.is_dual) ? `${levels} + Dual` : levels;
};

const familyCell = (entity: ContractedInsuranceEntity, family: ContractProgramFamilyOption) => {
  const coverage = familyCoverage(entity, family);
  if (coverage === 'all') return <AllCovered />;
  if (coverage === 'some') return contractedLevels(entity, family);
  return <span className="text-uniform-gray-400">—</span>;
};

const EntityCard = ({
  entity,
  families
}: {
  entity: ContractedInsuranceEntity;
  families: ContractProgramFamilyOption[];
}) => (
  <div className="rounded-lg border border-uniform-gray-200 p-3 dark:border-uniform-gray-700">
    <Typography className="flex flex-wrap items-baseline gap-x-2">
      <span className="rounded bg-uniform-gray-100 px-1.5 py-0.5 text-xs font-semibold dark:bg-uniform-gray-800">
        {entity.jurisdiction_abbreviation}
      </span>
      <span className="font-semibold">{entity.insurance_provider_name}</span>
      <span className="text-uniform-gray-500">{entity.insurance_type}</span>
    </Typography>
    <div className="mt-2 flex flex-wrap gap-2">
      {families
        .filter((family) => familyCoverage(entity, family) !== 'none')
        .map((family) => (
          <span
            key={family.id}
            title={family.name}
            className="rounded border border-uniform-gray-300 px-2 py-1 text-xs dark:border-uniform-gray-600">
            {familyTagLabel(family)}{' '}
            {familyCoverage(entity, family) === 'all' ? (
              <span className="font-semibold text-uniform-green-700 dark:text-uniform-green-400">
                All
              </span>
            ) : (
              <span className="font-semibold">{contractedLevels(entity, family)}</span>
            )}
          </span>
        ))}
    </div>
  </div>
);

const ContractInsuranceEntitiesPanel: FC<Props> = ({
  contract,
  filterOptions,
  compact = false,
  onHeightChange
}) => {
  const panelRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const panel = panelRef.current;
    if (!panel || !onHeightChange) return;
    const measure = () => onHeightChange(Math.ceil(panel.getBoundingClientRect().height));
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(panel);
    return () => observer.disconnect();
  }, [onHeightChange]);
  const { data: entities, isPending } = useDugoutContractInsuranceEntities(contract.id);

  const categories = useMemo(
    () =>
      groupFamiliesByCategory(
        filterOptions?.drug_categories ?? [],
        filterOptions?.program_families ?? []
      ),
    [filterOptions]
  );

  const headerClass = 'px-3 py-2 text-left font-medium text-uniform-gray-600 whitespace-nowrap';
  const cellClass = 'px-3 py-2 whitespace-nowrap';

  return (
    <div ref={panelRef} className="p-4">
      <div className="overflow-x-auto rounded-lg border border-uniform-gray-200 bg-white dark:border-uniform-gray-700 dark:bg-uniform-gray-900">
        <div className="px-4 pt-3 pb-2">
          <Typography weight="semibold">
            {compact
              ? `${contract.insurance_entity_count} insurance entities on`
              : 'Insurance entities on'}{' '}
            v{contract.version}{' '}
            {!compact && (
              <span className="font-normal text-uniform-gray-500">
                ({contract.insurance_entity_count})
              </span>
            )}
          </Typography>
          {!compact && (
            <Typography variant="caption" as="p" muted>
              Cells list contracted phase types: {PHASE_TYPE_LEGEND}. &ldquo;+ Dual&rdquo; = the
              Dual variants too. Only active programs count.
            </Typography>
          )}
        </div>

        {isPending && (
          <div className="flex justify-center p-4">
            <Spinner />
          </div>
        )}
        {!isPending && compact && (
          <div className="space-y-3 px-4 pb-4">
            {(entities ?? []).map((entity) => (
              <EntityCard
                key={entity.id}
                entity={entity}
                families={categories.flatMap(({ families }) => families)}
              />
            ))}
          </div>
        )}
        {!isPending && !compact && (
          <table className="min-w-full text-sm">
            <thead className="border-y border-uniform-gray-200 bg-uniform-gray-50 dark:border-uniform-gray-700 dark:bg-uniform-gray-800">
              <tr>
                <th className={headerClass} rowSpan={2}>
                  State
                </th>
                <th className={headerClass} rowSpan={2}>
                  Insurance provider
                </th>
                <th className={headerClass} rowSpan={2}>
                  Insurance type
                </th>
                {categories.map(({ category, families }) => (
                  <th
                    key={category.value}
                    className={`${headerClass} border-b border-l border-uniform-gray-200 dark:border-uniform-gray-700`}
                    colSpan={families.length}>
                    {category.label}
                  </th>
                ))}
              </tr>
              <tr>
                {categories.flatMap(({ families }) =>
                  families.map((family, index) => (
                    <th
                      key={family.id}
                      className={`${headerClass} ${index === 0 ? 'border-l border-uniform-gray-200 dark:border-uniform-gray-700' : ''}`}
                      title={family.name}>
                      {familyColumnLabel(family)}
                    </th>
                  ))
                )}
              </tr>
            </thead>
            <tbody>
              {(entities ?? []).map((entity) => (
                <tr
                  key={entity.id}
                  className="border-b border-uniform-gray-100 last:border-0 dark:border-uniform-gray-800">
                  <td className={`${cellClass} font-medium`}>{entity.jurisdiction_abbreviation}</td>
                  <td className={`${cellClass} font-medium`}>{entity.insurance_provider_name}</td>
                  <td className={cellClass}>{entity.insurance_type}</td>
                  {categories.flatMap(({ families }) =>
                    families.map((family, index) => (
                      <td
                        key={family.id}
                        className={`${cellClass} ${index === 0 ? 'border-l border-uniform-gray-100 dark:border-uniform-gray-800' : ''}`}>
                        {familyCell(entity, family)}
                      </td>
                    ))
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default memo(ContractInsuranceEntitiesPanel);
